import FieldRule from './FieldRule'
import Errors from '../Errors'
import {nl2br} from '@utils'

export default class Field
{
    constructor( name, options = {}, $validator = undefined ) {
        this.name = name // schema key (not the real data field name)
        this.$validator = $validator // ref to the parent vaidator object
        this.rules = {}

        // handles async values while provide cache
        this.async = {}
        this.asyncResult = {}
        //

        // apply default values
        Object.keys(options).forEach( key => {
            this[key] = options[key]
        })
    }

    static get RULE_OPERATOR_AND() {
      return 'and'
    }

    static get RULE_OPERATOR_OR() {
      return 'or'
    }

    validate( fieldName, value ) {
        let scenario = this.$validator.getScenario()

        let asyncValidations = []
        let warnings = new Errors()
        let errors = new Errors()
        let orErrors = []
        let orRuleCounter = 0

        if( typeof this.rules[scenario] !== 'undefined' ) {
            Object.keys( this.rules[scenario] ).forEach( operator => {
                this.rules[scenario][operator].forEach( (ruleItem, ruleName) => {

                    if ( ! Array.isArray(ruleItem) ) {
                        ruleItem = [ruleItem]
                    }

                    ruleItem.forEach(rule => {

                        if( rule.skipOnEmpty === true && ruleName !== 'required' && rule.isEmpty(value) ) {
                            return
                        }

                        let index = undefined
                        if( fieldName.indexOf('.') !== -1 ) {
                            let fieldNameParts = fieldName.split('.')
                            index = fieldNameParts[fieldNameParts.length - 2]
                        }

                        asyncValidations.push(Promise.resolve(rule.when( this.$validator, fieldName, index )).then( when => {

                            if( (Array.isArray(when) && (inArray(false, when) || inArray(undefined, when))) || (!Array.isArray(when) && when !== true) ) {
                                return // skip 'when' promise
                            }

                            if( operator === Field.RULE_OPERATOR_OR ) {
                                orRuleCounter++
                            }

                            let messages = ( rule.type === FieldRule.TYPE_WARNING )
                                                ? warnings
                                                : errors

                            const notAllowedPreAttrStateValidators = ['visible', 'disabled', 'readonly', 'default', 'sync']

                            let errorFieldLabel = this.$validator.form().getFieldLabel(fieldName)

                            if( inArray(ruleName, notAllowedPreAttrStateValidators) ) {
                                return
                            }
                            else if( ruleName === 'dynamic' ) {
                                return Promise.resolve(rule.params.callback( value, this.$validator, fieldName, index )).then( asyncResult => {

                                    let error = null
                                    if( typeof(asyncResult) !== 'boolean' ) {
                                        error = asyncResult
                                    } else {
                                        if( true === Boolean(asyncResult) ) {
                                            return
                                        }
                                        else if( false === Boolean(asyncResult) && rule.errorMessage ) {
                                            if( typeof rule.errorMessage === 'function' ) {
                                                error = rule.errorMessage.bind(this.$validator)( errorFieldLabel, rule.params, this.$validator )
                                            } else {
                                                error = rule.errorMessage
                                            }
                                        }
                                    }

                                    if( null === error ) {
                                        return Promise.reject(new Error('[Validator]: Invalid callback response from rule: "'+ ruleName +'".'))
                                    }

                                    if( operator === Field.RULE_OPERATOR_OR ) {
                                        orErrors.push( error )
                                    } else { // on 'and'
                                        messages.add( fieldName, error )
                                    }
                                })
                            }

                            return this.$validator.runValidation(ruleName, fieldName, value, rule.params, rule).then( ruleResponse => {
                                let errorMessage
                                let isValid = Boolean(ruleResponse)

                                if ( // support for the multiple messages
                                    typeOf(ruleResponse) === 'object' &&
                                    typeof ruleResponse.result !== 'undefined' &&
                                    typeof ruleResponse.message !== 'undefined'
                                ) {
                                    isValid = Boolean(ruleResponse.result)
                                    errorMessage = ruleResponse.message
                                }

                                let not = rule.not

                                if( not === isValid )
                                {
                                    // get error message
                                    let messageKey = (not ? 'not_' : '') + ruleName

                                    if( rule.errorMessage ) {
                                        if( typeof rule.errorMessage === 'function' ) {
                                            errorMessage = rule.errorMessage( errorFieldLabel, rule.params, this.$validator )
                                        } else {
                                            errorMessage = rule.errorMessage
                                        }
                                    } else if( typeof errorMessage === 'undefined' ) {
                                        errorMessage = ( typeof this.$validator.$errorMessages[ messageKey ] !== 'undefined' )
                                                            ? this.$validator.$errorMessages[ messageKey ].bind(this.$validator)( errorFieldLabel, rule.params, this.$validator )
                                                            : this.$validator.$errorMessages._default( errorFieldLabel )
                                    }
                                    //

                                    if( operator === Field.RULE_OPERATOR_OR ) {
                                        orErrors.push( errorMessage )
                                    } else { // on 'and'
                                        messages.add( fieldName, errorMessage )
                                    }
                                }
                            })
                        }))

                    })
                })
            })
        }

        return Promise.all(asyncValidations).then( result => {

            if( orRuleCounter > 0 && orRuleCounter === orErrors.length ) {
                let message = this.$validator.$errorMessages._atLeastOneRule( fieldName ) + " \n - " + orErrors.join("\n - ")

                errors.add(
                    fieldName,
                    nl2br(message)
                )
            }

            return {fieldName, errors, warnings}
        })
    }

    isRequired( field ) {
        return this._getAttributeState(field, 'required')
    }

    isDisabled( field ) {
        return false === this.isVisible(field) || this._getAttributeState(field, 'disabled')
    }

    isReadonly( field ) {
        return this._getAttributeState(field, 'readonly')
    }

    /**
     * 1. 'visible' rule is not described = true
     * 2. 'visible' described but without 'when' = true
     * 3. 'visible' + 'when' described = true if 'when' is true
     * 4. .not('visible') = false
    */
    isVisible( field ) {
        let rules = this.getScenarioRules( Field.RULE_OPERATOR_AND )

        // for disabled, readonly and required - this must return false
        if( ! rules.has('visible') )
            return true

        return this._getAttributeState(field, 'visible')
    }

    sync( field ) {
        this.getRuleParamPromise('sync', field, 'fields').then( withFields => {
            if( ! isArray(withFields) ) {
                return
            }

            let realWithFields = []
            this.$validator.applyCallbackOnRealFields( withFields, (schemaField, fieldName) => {
                realWithFields.push(fieldName)
            })

            this.$validator.syncUnwatchers.push(
                this.$validator.form().getOptions().vm.$watch( this.$validator.form().getOptions().dataProp + '.' + field, () => {
                    realWithFields.forEach(fieldName => {
                        this.$validator.validateField(fieldName, false)
                    })
                })
            )
        })
    }

    _getAttributeState( field, attribute ) {
        let rules = this.getScenarioRules( Field.RULE_OPERATOR_AND )

        if( ! rules.has(attribute) )
            return false

        let ruleItem = rules.get(attribute)

        if ( ! Array.isArray(ruleItem) ) {
            ruleItem = [ruleItem]
        }

        let _test = (rule, index) => {
            if( ! rule.hasWhen() ) {
                if( rule.not === true ) {
                    return false
                }

                return true
            }

            let result = this._handlePromise(field, attribute + '-' + index + '-when', () => {
                return rule.when( this.$validator, field )
            })

            if( rule.not === true ) {
                return ! Boolean(result)
            }

            return result
        }

        let result = true

        ruleItem.forEach((rule, index) => {
            if( ! _test(rule, index) ) {
                result = false
            }
        })

        return result
    }

    _handlePromise( field, attribute, getUserPromise ) {

        let key = `${field}-${attribute}`

        if( typeof this.async[key] === 'undefined' ) {

            let promise = Promise.resolve(getUserPromise())

            this.async[key] = promise

            promise.then( promiseResult => {
                Vue.set(this.asyncResult, key, promiseResult)

                // fix for Vue reactivity cache
                setTimeout(() => {
                    delete this.async[key]
                }, 1)
            })
        }

        if( Array.isArray(this.asyncResult[key]) ) {
            return !inArray(false, this.asyncResult[key]) && !inArray(undefined, this.asyncResult[key]) // handle Promise.all
        }

        return this.asyncResult[key] === true
    }

    getRuleParamPromise( ruleName, field, param ) {
        let rules = this.getScenarioRules( Field.RULE_OPERATOR_AND )

        if( ! rules.has(ruleName) )
            return

        let ruleItem = rules.get(ruleName)

        if ( ! Array.isArray(ruleItem) ) {
            ruleItem = [ruleItem]
        }

        let _getParam = (rule) => {

            if( ! rule.hasWhen() ) {
                return Promise.resolve(rule.params[param])
            }

            return Promise.resolve(rule.when( this.$validator, field )).then(promiseResult => {
                if(promiseResult) {
                    return rule.params[param]
                }

                return undefined
            })
        }

        // every next valid rule will rewrite the final value

        let promises = []

        ruleItem.forEach(rule => {
            let tmpResult = _getParam(rule)

            if( tmpResult !== undefined ) {
                promises.push(tmpResult)
            }
        })

        // https://github.com/amrayn/allsettled-polyfill
        let allSettled = (promises) => Promise.all(promises.map(p => p
            .then(v => ({
                status: 'fulfilled',
                value: v
            }))
            .catch(e => ({
                status: 'rejected',
                reason: e,
            }))
        ))

        return allSettled(promises).then( promisesResult => {

            let result = undefined

            promisesResult.forEach(promiseResult => {
                if( promiseResult.status === 'fulfilled' && promiseResult.value !== undefined ) {
                    result = promiseResult.value
                }
            })

            return result
        })
    }

    getRuleParam( ruleName, field, param, instantly = false ) {
        let rules = this.getScenarioRules( Field.RULE_OPERATOR_AND )

        if( ! rules.has(ruleName) )
            return

        let ruleItem = rules.get(ruleName)

        if ( ! Array.isArray(ruleItem) ) {
            ruleItem = [ruleItem]
        }

        let _getParam = (rule, index) => {
            if( instantly === true || ! rule.hasWhen() ) {
                return rule.params[param]
            }

            let when = this._handlePromise(field, ruleName + '-' + index + '-when', () => {
                return rule.when( this.$validator, field )
            })

            if( when ) {
                return rule.params[param]
            }

            return undefined
        }

        // every next valid rule will rewrite the final value

        let result = undefined

        ruleItem.forEach((rule, index) => {

            let tmpResult = _getParam(rule, index)

            if( tmpResult !== undefined ) {
                result = tmpResult
            }

        })

        return result
    }

    setValidator( object ) {
        this.$validator = object
        return this
    }

    getScenarioRules( operator = undefined ) {
        let scenario = this.$validator.getScenario()

        if( typeof this.rules[scenario] === 'undefined' ) {
            return new Map()
        }

        if( operator === undefined ) {
            return this.rules[scenario]
        }

        if( typeof this.rules[scenario][operator] === 'undefined' ) {
            return new Map()
        }

        return this.rules[scenario][operator]
    }

    setRules( rules ) {
        this.rules = rules
        return this
    }

    addRules( scenarios, operator, rules ) {
        if( ! Array.isArray(scenarios))
            scenarios = [scenarios]

        scenarios.forEach( scenario => {
            Object.keys(rules).forEach( validatorName => {
                let rule = rules[validatorName]
                this.addRule(scenario, operator, rule)
            })
        })

        return this
    }

    addRule( scenario, operator, rule ) {

        if( typeof this.rules[scenario] === 'undefined' )
        {
            this.rules[scenario] = {
                and: new Map(),
                or: new Map()
            }
        }

        if( ! Array.isArray(rule) ) {
            this.rules[scenario][operator].set(rule.name, rule)
            return this
        }

        let ruleName = null

        rule.forEach(ruleItem => {
            if ( ! (ruleItem instanceof FieldRule) ) {
                console.warn('[Validator]: Field rules must be instance of `FieldRule` class.')
            }

            ruleName = ruleItem.name
        })

        /*
         * In case we have:
         *
         * $('a').max(5),
         * $('a').max(1).when(...)
         *
         * instead of:
         *
         * $('a').max(5).max(1, {when: ...})
         *
         * The rules will come as a separate rule sets.
         * Therefore we need to merge them with an existing ones.
         */

        let mergedRules = rule

        if( this.rules[scenario][operator].has(ruleName) )
        {
            let existingRules = this.rules[scenario][operator].get(ruleName)

            if( Array.isArray(existingRules) )
            {
                mergedRules = [...existingRules, ...rule]
            }
            else
            {
                // its a single existing rule
                console.warn('[Validator:Filed]: Trying to set an array of rules to a existing single rule.')
            }
        }

        this.rules[scenario][operator].set(ruleName, mergedRules)

        return this
    }
}
